/**
 * Instance-scope role hierarchy for the chat client.
 *
 * MIRRORS backend/src/utils/roleColors.js (ROLE_RANK / canRestrictRole). The
 * server is authoritative — this copy exists only so the UI can hide controls
 * that the server would refuse anyway. Keep the two in sync.
 *
 * Roles arrive in mixed case here: the socket presence payload lowercases them
 * ("owner"/"admin"/"guest") while the REST membership keeps them uppercase
 * ("OWNER"), so every comparison is case-insensitive.
 */

// Higher number = more privileged. PLATFORM_* roles are panel-scope only and
// never reach the chat, so they have no rank and fall through to GUEST.
export const ROLE_RANK = {
  OWNER: 3,
  ADMIN: 2,
  MEMBER: 1,
  GUEST: 0,
}

/**
 * Rank for a role, case-insensitively. Unknown/missing roles rank as GUEST so a
 * bad value can never grant privilege.
 * @param {string|null|undefined} role
 * @returns {number}
 */
export const getRoleRank = (role) => {
  const key = String(role || "").trim().toUpperCase()
  return Object.prototype.hasOwnProperty.call(ROLE_RANK, key)
    ? ROLE_RANK[key]
    : ROLE_RANK.GUEST
}

/**
 * Whether `actorRole` may apply an OUTWARD restriction (one that limits what the
 * target can do or see) to `targetRole`: peers and below, never above.
 *
 * Used to gate the cam-block ("eye") toggle so a user can't blind a moderator.
 * Self-protective local preferences like ignore/mute are NOT gated by this.
 *
 * @param {string|null|undefined} actorRole
 * @param {string|null|undefined} targetRole
 * @returns {boolean}
 */
export const canRestrictRole = (actorRole, targetRole) =>
  getRoleRank(actorRole) >= getRoleRank(targetRole)
